import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import type { AnomalyRow } from '@services/activity.service';
import { formatActivityDateTime } from '../formatActivityDateTime';
import { SEVERITY_VARIANT } from './anomalySeverity';

interface AnomalyTableProps {
  items: AnomalyRow[];
  loading?: boolean;
  onResolve: (row: AnomalyRow) => void;
}

const AnomalyTable: React.FC<AnomalyTableProps> = ({ items, loading, onResolve }) => {
  const { t } = useTranslation();

  const columns = useMemo<ColumnDef<AnomalyRow, any>[]>(
    () => [
      {
        id: 'time',
        accessorKey: 'created_at',
        header: t('audit.time'),
        cell: ({ row }) => (
          <span className="revy-figure whitespace-nowrap">
            {formatActivityDateTime(row.original.created_at)}
          </span>
        ),
      },
      {
        id: 'type',
        accessorKey: 'event_type',
        header: t('anomalies.type'),
        cell: ({ row }) => <code>{row.original.event_type}</code>,
      },
      {
        id: 'severity',
        accessorKey: 'severity',
        header: t('anomalies.severity'),
        cell: ({ row }) => (
          <Badge variant={SEVERITY_VARIANT[row.original.severity]} className="revy-cjk">
            {t(`anomalies.severityValues.${row.original.severity}`)}
          </Badge>
        ),
      },
      {
        id: 'path',
        header: t('anomalies.path'),
        enableSorting: false,
        size: 480,
        cell: ({ row }) => {
          const path = row.original.request_method
            ? `${row.original.request_method} ${row.original.request_path ?? ''}`
            : '-';
          return (
            // Fixed width rather than a max: paths run to full document URLs, so
            // this column holds its size and the table scrolls instead of
            // squeezing it.
            <span
              title={path}
              className="revy-cjk block w-[30rem] text-xs leading-[1.7] [overflow-wrap:anywhere]"
            >
              {path}
            </span>
          );
        },
      },
      {
        id: 'status',
        accessorKey: 'response_status',
        header: 'HTTP',
        cell: ({ row }) => (
          <span className="revy-figure">{row.original.response_status ?? '-'}</span>
        ),
      },
      {
        id: 'ip',
        accessorKey: 'ip_address',
        header: 'IP',
        cell: ({ row }) => <span className="revy-figure">{row.original.ip_address ?? '-'}</span>,
      },
      {
        id: 'resolved',
        accessorKey: 'resolved',
        header: t('anomalies.status'),
        cell: ({ row }) =>
          row.original.resolved ? (
            <Badge variant="success" className="revy-cjk">
              {t('anomalies.resolved')}
            </Badge>
          ) : (
            <Badge variant="warning" className="revy-cjk">
              {t('anomalies.unresolved')}
            </Badge>
          ),
      },
      {
        id: 'actions',
        header: t('common.actions'),
        enableSorting: false,
        cell: ({ row }) =>
          row.original.resolved ? (
            <span className="revy-figure whitespace-nowrap text-xs leading-[1.7] text-muted-foreground">
              {formatActivityDateTime(row.original.resolved_at)}
            </span>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => onResolve(row.original)}>
              <span className="revy-cjk">{t('anomalies.markResolved')}</span>
            </Button>
          ),
      },
    ],
    [t, onResolve],
  );

  return (
    <DataTable
      paginated
      columns={columns}
      data={items}
      loading={loading}
      loadingLabel={t('common.loading')}
      getRowId={(row) => String(row.id)}
      empty={
        <span className="revy-cjk text-sm leading-[1.6] text-muted-foreground">
          {t('anomalies.noMatchingAnomalies')}
        </span>
      }
    />
  );
};

export default AnomalyTable;
